/**
 * EventMiniSparkline
 * ==================
 *
 * Tiny sparkline for an event STREAM (used in the Rows + Compact list views).
 * Mirrors MiniSparkline's minimalism but encodes more at a glance:
 *
 *   - SIGNED score: magnitude = rank (significance), sign = sentiment, so
 *     positive events sit ABOVE a faint zero midline and negative ones BELOW.
 *   - Each event is a small dot colored by its sentiment (green/grey/red).
 *
 * No axes, grid, tooltip — just the shape + color play.
 */
import React, { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import { MAX_CHART_POINTS } from '../../containers/charts/signalBinsMini';

const EventMiniSparkline = ({ points, height = 36, width }) => {
  const theme = useTheme();

  const sentimentColor = (s) => (
    s === 'positive' ? theme.palette.success.main
      : s === 'negative' ? theme.palette.error.main
        : theme.palette.text.secondary
  );

  const series = useMemo(() => {
    const all = Array.isArray(points) ? points : [];
    const pts = all.length > MAX_CHART_POINTS ? all.slice(-MAX_CHART_POINTS) : all;
    return pts.map((p, i) => {
      const rank = Number(p?.rank) || 0;
      const sentiment = p?.sentiment;
      const polarity = p?.polarity;
      const sign = sentiment === 'positive' ? 1
        : sentiment === 'negative' ? -1
          : (typeof polarity === 'number' ? Math.sign(polarity) : 0);
      return { x: i, y: rank * sign, sentiment };
    });
  }, [points]);

  if (series.length === 0) {
    return (
      <Box sx={{
        width: width || '100%', height,
        color: 'text.disabled', fontSize: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        —
      </Box>
    );
  }

  // Dots colored by sentiment, drawn over the line.
  const PointsLayer = ({ points: pts }) => (
    <g>
      {pts.map((pt) => (
        <circle
          key={pt.id}
          cx={pt.x}
          cy={pt.y}
          r={2.5}
          fill={sentimentColor(pt.data?.sentiment)}
        />
      ))}
    </g>
  );

  return (
    <Box sx={{ width: width || '100%', height }}>
      <ResponsiveLine
        data={[{ id: 'score', data: series }]}
        margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: -100, max: 100 }}
        curve="monotoneX"
        enableArea
        areaBaselineValue={0}
        areaOpacity={0.1}
        enablePoints={false}
        colors={[theme.palette.text.secondary]}
        lineWidth={1.25}
        enableGridX={false}
        enableGridY={false}
        axisTop={null}
        axisRight={null}
        axisBottom={null}
        axisLeft={null}
        markers={[{
          axis: 'y', value: 0,
          lineStyle: { stroke: theme.palette.divider, strokeWidth: 1, strokeDasharray: '2 2' },
        }]}
        layers={['markers', 'areas', 'lines', PointsLayer]}
        animate={false}
        isInteractive={false}
      />
    </Box>
  );
};

export default EventMiniSparkline;
